import { Fragment, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XMarkIcon, EnvelopeIcon } from '@heroicons/react/24/outline'
import { useQuery, useMutation } from '@apollo/client'
import { GET_ROLES } from '../graphql/queries'
import { INVITE_USER } from '../graphql/mutations'
import toast from 'react-hot-toast'

interface InviteUserDialogProps {
  isOpen: boolean
  onClose: () => void
  onInvited: () => void
}

const InviteUserDialog: React.FC<InviteUserDialogProps> = ({ isOpen, onClose, onInvited }) => {
  const [email, setEmail] = useState('')
  const [selectedRoleIds, setSelectedRoleIds] = useState<number[]>([])

  const { data: rolesData, loading: rolesLoading } = useQuery(GET_ROLES, {
    variables: { filters: { active: true } },
    skip: !isOpen,
  })

  const [inviteUser, { loading: inviting }] = useMutation(INVITE_USER, {
    onCompleted: (data) => {
      toast.success(`Invitation sent to ${data.inviteUser.email}`)
      handleClose()
      onInvited()
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to invite user')
    },
  })

  const handleClose = () => {
    setEmail('')
    setSelectedRoleIds([])
    onClose()
  }

  const toggleRole = (roleId: number) => {
    setSelectedRoleIds((prev) =>
      prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId]
    )
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.trim()) {
      toast.error('Email is required')
      return
    }
    if (selectedRoleIds.length === 0) {
      toast.error('Select at least one role')
      return
    }
    inviteUser({ variables: { email: email.trim(), role_ids: selectedRoleIds } })
  }

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-4">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900 flex items-center gap-2"
                  >
                    <EnvelopeIcon className="h-5 w-5" />
                    Invite User
                  </Dialog.Title>
                  <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <p className="text-sm text-gray-500 mb-4">
                  The user receives an email with a link to set their own name and password.
                  The invitation is valid for 7 days.
                </p>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="invite-email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email address
                    </label>
                    <input
                      type="email"
                      id="invite-email"
                      className="input-field"
                      placeholder="user@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoFocus
                    />
                  </div>

                  <div>
                    <span className="block text-sm font-medium text-gray-700 mb-1">Roles</span>
                    {rolesLoading ? (
                      <p className="text-sm text-gray-500">Loading roles…</p>
                    ) : (
                      <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded-md p-3">
                        {(rolesData?.roles || []).map((role: any) => (
                          <label key={role.id} className="flex items-start gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              className="mt-1"
                              checked={selectedRoleIds.includes(role.id)}
                              onChange={() => toggleRole(role.id)}
                            />
                            <span>
                              <span className="block text-sm font-medium text-gray-900">{role.role}</span>
                              {role.description && (
                                <span className="block text-xs text-gray-500">{role.description}</span>
                              )}
                            </span>
                          </label>
                        ))}
                        {!rolesLoading && (rolesData?.roles || []).length === 0 && (
                          <p className="text-sm text-gray-500">No active roles available</p>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end gap-2 pt-2">
                    <button type="button" className="btn-secondary" onClick={handleClose}>
                      Cancel
                    </button>
                    <button type="submit" className="btn-primary" disabled={inviting}>
                      {inviting ? 'Sending…' : 'Send invitation'}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export default InviteUserDialog
